import React from 'react';

/**
 * Renders a published consent-notice "document" (server/modules/consent-notice/noticeRender.js):
 * paragraphs of text/link segments and the itemised data table. Pure presentation — every
 * word comes from the server, so the candidate sees exactly the text that was hashed and
 * stored. Used by the candidate pop-up.
 */

const Segments = ({ segments }) =>
  segments.map((seg, i) =>
    seg.type === 'link' ? (
      <a
        // eslint-disable-next-line react/no-array-index-key
        key={i}
        href={seg.href}
        target={seg.href.startsWith('http') ? '_blank' : undefined}
        rel="noopener noreferrer"
        className="font-medium text-electric-blue underline underline-offset-2 break-all hover:text-indigo-700"
      >
        {seg.text}
      </a>
    ) : (
      // eslint-disable-next-line react/no-array-index-key
      <React.Fragment key={i}>{seg.text}</React.Fragment>
    )
  );

const BulletList = ({ items }) => (
  <ul className="space-y-1.5">
    {items.map((item, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <li key={i} className="flex gap-2">
        <span className="mt-[0.55em] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-electric-blue/70" aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const DataTable = ({ block }) => (
  <section aria-labelledby="consent-notice-table-heading" className="my-5">
    <h3 id="consent-notice-table-heading" className="mb-2.5 text-sm font-semibold text-gray-900">
      {block.heading}
    </h3>

    {/* Wide screens: the three-column table from the notice. */}
    <div className="hidden overflow-hidden rounded-xl border border-gray-200 md:block">
      <table className="w-full table-fixed border-collapse text-left text-[13px] leading-relaxed">
        <thead className="bg-gray-50 text-gray-900">
          <tr>
            <th scope="col" className="w-[28%] border-b border-gray-200 px-3.5 py-2.5 font-semibold">{block.headers.data}</th>
            <th scope="col" className="border-b border-l border-gray-200 px-3.5 py-2.5 font-semibold">{block.headers.purpose}</th>
            <th scope="col" className="w-[24%] border-b border-l border-gray-200 px-3.5 py-2.5 font-semibold">{block.headers.ground}</th>
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={i} className="align-top">
              <td className="px-3.5 py-3">
                <span className="mb-2 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-amber-200">
                  {row.label}
                </span>
                <BulletList items={row.dataElements} />
              </td>
              <td className="border-l border-gray-200 px-3.5 py-3"><BulletList items={row.purposes} /></td>
              <td className="border-l border-gray-200 px-3.5 py-3">{row.ground}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Phones: the same content stacked, so nothing scrolls sideways. */}
    <div className="space-y-3 md:hidden">
      {block.rows.map((row, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className="divide-y divide-gray-100 rounded-xl border border-gray-200 text-[13px] leading-relaxed">
          <div className="p-3">
            <p className="mb-1.5 flex items-center gap-2 font-semibold text-gray-900">
              {block.headers.data}
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-amber-200">
                {row.label}
              </span>
            </p>
            <BulletList items={row.dataElements} />
          </div>
          <div className="p-3">
            <p className="mb-1.5 font-semibold text-gray-900">{block.headers.purpose}</p>
            <BulletList items={row.purposes} />
          </div>
          <div className="p-3">
            <p className="mb-1 font-semibold text-gray-900">{block.headers.ground}</p>
            <p>{row.ground}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const ConsentNoticeDocument = ({ document: doc, dir = 'ltr', lang }) => {
  if (!doc) return null;
  return (
    <div dir={dir} lang={lang} className="text-sm leading-relaxed text-gray-700">
      {doc.blocks.map((block, i) =>
        block.type === 'table' ? (
          // eslint-disable-next-line react/no-array-index-key
          <DataTable key={i} block={block} />
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <p key={i} className="mb-3">
            <Segments segments={block.segments} />
          </p>
        )
      )}
    </div>
  );
};

export default ConsentNoticeDocument;
